
import { supabase } from "@/integrations/supabase/client";
import { Question } from "@/types/test";
import { toast } from "@/components/ui/use-toast";
import { QuestionDistribution } from "../question-generation/QuestionDistributionForm";
import { SkillConfig } from "../SkillBasedGenerationDialog";

export interface SkillBasedDistribution {
  skillConfigs: SkillConfig[];
}

export const generateQuestions = async (
  jobTitle: string,
  jobDescription: string,
  keyResponsibilities: string,
  yearsOfExperience: string,
  keySkills: string[],
  distribution?: QuestionDistribution | SkillBasedDistribution,
  maxRetries = 3,
  retryCount = 0
): Promise<Question[]> => {
  try {
    console.log('Generating questions with params:', {
      jobTitle,
      jobDescription,
      keyResponsibilities,
      yearsOfExperience,
      keySkills,
      distribution
    });

    // Check if we're using the new skill-based distribution format
    const isSkillBased = distribution && 'skillConfigs' in distribution;
    
    const { data, error } = await supabase.functions.invoke('generate-test-questions', {
      body: {
        jobTitle,
        jobDescription,
        keyResponsibilities,
        yearsOfExperience: parseInt(yearsOfExperience) || 0,
        keySkills,
        distribution,
        isSkillBased: isSkillBased
      },
    });

    if (error) {
      console.error('Error generating questions:', error);
      
      if (error.message.includes('Failed to fetch') && retryCount < maxRetries) {
        return new Promise(resolve => 
          setTimeout(() => 
            resolve(generateQuestions(
              jobTitle, 
              jobDescription, 
              keyResponsibilities, 
              yearsOfExperience,
              keySkills,
              distribution,
              maxRetries, 
              retryCount + 1
            )), 
            1000 * (retryCount + 1)
          )
        );
      }

      throw error;
    }

    return data.questions;
  } catch (error: any) {
    console.error('Error in generateQuestions:', error);
    
    let errorMessage = 'Failed to generate questions. ';
    if (error.message.includes('Failed to fetch')) {
      errorMessage += 'Please check your internet connection and try again.';
    } else {
      errorMessage += error.message || 'Please try again later.';
    }
    
    toast({
      title: "Error",
      description: errorMessage,
      variant: "destructive",
    });
    
    throw error;
  }
};

export const regenerateQuestion = async (
  question: Question,
  jobTitle: string,
  jobDescription: string,
  keyResponsibilities: string,
  yearsOfExperience: string,
  keySkills: string[],
  maxRetries = 3,
  retryCount = 0
): Promise<Question> => {
  try {
    console.log('Regenerating question with params:', {
      questionType: question.question_type,
      jobTitle,
      jobDescription,
      keyResponsibilities,
      yearsOfExperience,
      keySkills,
      skill: question.skill,
      difficulty: question.difficulty
    });

    // Create a distribution object with only the question type we want to regenerate
    const distribution: Record<string, number> = {
      multiple_choice: 0,
      essay: 0,
      audio_response: 0,
      video_response: 0,
      coding: 0,
    };
    
    // Set the count for the current question type to 1
    distribution[question.question_type] = 1;

    const { data, error } = await supabase.functions.invoke('generate-test-questions', {
      body: {
        jobTitle,
        jobDescription,
        keyResponsibilities,
        yearsOfExperience: parseInt(yearsOfExperience) || 0,
        keySkills,
        distribution,
        targetSkill: question.skill, // Pass the current skill to preserve it
        targetDifficulty: question.difficulty // Pass the current difficulty to preserve it
      },
    });

    if (error) {
      console.error('Error regenerating question:', error);
      
      if (error.message.includes('Failed to fetch') && retryCount < maxRetries) {
        return new Promise(resolve => 
          setTimeout(() => 
            resolve(regenerateQuestion(
              question,
              jobTitle, 
              jobDescription, 
              keyResponsibilities, 
              yearsOfExperience,
              keySkills,
              maxRetries, 
              retryCount + 1
            )), 
            1000 * (retryCount + 1)
          )
        );
      }

      throw error;
    }

    if (!data.questions || data.questions.length === 0) {
      throw new Error('No questions were generated');
    }

    // Take the first generated question and preserve the original ID and order
    const regeneratedQuestion = {
      ...data.questions[0],
      id: question.id,
      order: question.order,
      // Ensure skill and difficulty are preserved if new ones aren't provided
      skill: data.questions[0].skill || question.skill,
      difficulty: data.questions[0].difficulty || question.difficulty
    };

    return regeneratedQuestion;
  } catch (error: any) {
    console.error('Error in regenerateQuestion:', error);
    
    let errorMessage = 'Failed to regenerate question. ';
    if (error.message.includes('Failed to fetch')) {
      errorMessage += 'Please check your internet connection and try again.';
    } else {
      errorMessage += error.message || 'Please try again later.';
    }
    
    toast({
      title: "Error",
      description: errorMessage,
      variant: "destructive",
    });
    
    throw error;
  }
};

export const regenerateQuestionWithFeedback = async (
  question: Question,
  userFeedback: string,
  jobTitle: string,
  jobDescription: string,
  keyResponsibilities: string,
  yearsOfExperience: string,
  keySkills: string[],
  maxRetries = 3,
  retryCount = 0
): Promise<Question> => {
  try {
    console.log('Regenerating question with feedback:', {
      questionType: question.question_type,
      userFeedback,
      jobTitle,
      skill: question.skill,
      difficulty: question.difficulty
    });

    const { data, error } = await supabase.functions.invoke('regenerate-question-with-feedback', {
      body: {
        currentQuestion: question,
        userFeedback,
        jobTitle,
        jobDescription,
        keyResponsibilities,
        yearsOfExperience,
        keySkills
      },
    });

    if (error) {
      console.error('Error regenerating question with feedback:', error);
      
      if (error.message.includes('Failed to fetch') && retryCount < maxRetries) {
        return new Promise(resolve => 
          setTimeout(() => 
            resolve(regenerateQuestionWithFeedback(
              question,
              userFeedback,
              jobTitle, 
              jobDescription, 
              keyResponsibilities, 
              yearsOfExperience,
              keySkills,
              maxRetries, 
              retryCount + 1
            )), 
            1000 * (retryCount + 1)
          )
        );
      }

      throw error;
    }

    if (!data.question) {
      throw new Error('No question was regenerated');
    }

    // Preserve the original ID and order
    const regeneratedQuestion = {
      ...data.question,
      id: question.id,
      order: question.order
    };

    return regeneratedQuestion;
  } catch (error: any) {
    console.error('Error in regenerateQuestionWithFeedback:', error);
    
    let errorMessage = 'Failed to regenerate question. ';
    if (error.message.includes('Failed to fetch')) {
      errorMessage += 'Please check your internet connection and try again.';
    } else {
      errorMessage += error.message || 'Please try again later.';
    }
    
    toast({
      title: "Error",
      description: errorMessage,
      variant: "destructive",
    });
    
    throw error;
  }
};

export const generateMarkscheme = async (
  question: Question,
  jobContext?: {
    title?: string;
    description?: string;
    keySkills?: string[];
    yearsOfExperience?: string;
  },
  maxRetries = 3,
  retryCount = 0
): Promise<any> => {
  try {
    console.log('Generating markscheme for question:', {
      questionId: question.id,
      questionType: question.question_type,
      skill: question.skill,
      marks: question.marks
    });

    const { data, error } = await supabase.functions.invoke('generate-markscheme', {
      body: {
        question,
        jobContext
      },
    });

    if (error) {
      console.error('Error generating markscheme:', error);
      
      if (error.message.includes('Failed to fetch') && retryCount < maxRetries) {
        return new Promise(resolve => 
          setTimeout(() => 
            resolve(generateMarkscheme(
              question,
              jobContext,
              maxRetries, 
              retryCount + 1
            )), 
            1000 * (retryCount + 1)
          )
        );
      }

      throw error;
    }

    if (!data.markscheme) {
      throw new Error('No markscheme was generated');
    }

    return data.markscheme;
  } catch (error: any) {
    console.error('Error in generateMarkscheme:', error);
    
    let errorMessage = 'Failed to generate markscheme. ';
    if (error.message.includes('Failed to fetch')) {
      errorMessage += 'Please check your internet connection and try again.';
    } else {
      errorMessage += error.message || 'Please try again later.';
    }
    
    toast({
      title: "Error",
      description: errorMessage,
      variant: "destructive",
    });
    
    throw error;
  }
};
